import type { JobSeekerProfileStructuredDataSalaryType } from "../../../../../../../../../__generated__/types.gqlgenerated";
import type { EnrichedJobDetailsPreference, SalaryInfoModel } from "../../types";

import { type GettextInterface, useI18n, useLocale } from "../../../../../../../../../shared/I18n";
import { useTareData } from "../../../../../../../../../shared/native-modules/tare/TareDataContext";
import { getLocaleCurrency } from "../../../../../../../../../shared/util/salary/getLocaleCurrency";
import { parseLocaleCurrencyAmount } from "../../../../../../../../../shared/util/salary/parseLocaleCurrencyAmount";
import Sentiment from "../../../../../../../../../shared/util/salary/Sentiment";
import { useViewjobOneGraphModel } from "../../../../../../../context/ViewJobDataContext";
import {
  type ContextModalProps,
  useModalContext,
} from "../../../../context/JMUIModalContextProvider";
import { validatePayRange } from "../../../common/CollectionModal/forms/MinimumPayForm/validation";
import { getTileVariant } from "../../../common/Tile";
import { useAddMinimumPayPreference } from "../../graphql/useAddMinimumPayPreference";
import { useResetPreference } from "../../graphql/useResetPreference";
import { useSavePreferenceAndValidateResult } from "../../graphql/useSavePreferenceAndValidateResult";
import { logIfQuestionReasked } from "../ReaskedQuestionTracker";
import { SalaryTypeToValue } from "../salary";
import { SegmentationSection } from "../usePreferenceSections";

import { log as logInteractiveAttributeInteraction } from "@indeed/signals-schema/com/indeed/signals/schema/profile/InteractiveAttributeInteraction";
import { log as logPayPreferenceCollection } from "@indeed/signals-schema/com/indeed/signals/schema/profile/PayPreferenceCollection";
import {
  log as logPreferenceSentimentClick,
  type PreferenceSentimentClick,
} from "@indeed/signals-schema/com/indeed/signals/schema/profile/PreferenceSentimentClick";
import {
  AvroOptional,
  log as logPreferenceSentimentSeen,
} from "@indeed/signals-schema/com/indeed/signals/schema/profile/PreferenceSentimentSeen";
import { useEffect, useMemo, useReducer } from "react";

export const enum PreferenceType {
  JOB_TYPE = "jobTypes",
  SHIFT_AND_SCHEDULE = "shiftSchedule",
  SALARY = "salary",
  SEGMENTATION = "segmentation",
  JOB_TYPE_AND_SHIFT_SCHEDULE = "jobTypesAndShiftSchedule",
  OTHER_DETAILS = "otherDetails",
}

export const getSegmentationQuestion = (
  label: string,
  sectionTitle: string,
  i18n: GettextInterface,
) => {
  switch (sectionTitle.toLowerCase()) {
    case SegmentationSection.WORK_SETTING.toLowerCase():
      return i18n.gettext("Are you interested in working in a {0} setting?", [label]);
    case SegmentationSection.MEDICAL_SPECIALTY.toLowerCase():
      return i18n.gettext("Are you interested in working in {0}?", [label]);
    case SegmentationSection.PATIENT_DEMOGRAPHIC.toLowerCase():
      return i18n.gettext("Are you interested in working with {0}?", [label]);
    case SegmentationSection.TEACHING_DETAILS.toLowerCase():
      return i18n.gettext("Are you interested in teaching {0}?", [label]);
    case SegmentationSection.TRAVEL_REQUIREMENTS.toLowerCase():
      return i18n.gettext("Are you interested in a {0} role?", [label]);
    case SegmentationSection.TRUCKING_DETAILS.toLowerCase():
      return i18n.gettext("Are you interested in a {0} trucking role?", [label]);
    default:
      return getDefaultQuestion(label, i18n);
  }
};

export const getDefaultQuestion = (label: string, i18n: GettextInterface) => {
  return i18n.gettext("Are you willing to work {0}?", [label]);
};

export const getPreferenceCollectionQuestion = (
  preferenceType: PreferenceType,
  label: string,
  sectionTitle: string,
  i18n: GettextInterface,
): string => {
  switch (preferenceType) {
    case PreferenceType.SALARY:
      return i18n.gettext("What is your [minimum pay|{0}] preference?", ["attribute"]);
    case PreferenceType.JOB_TYPE:
      return i18n.gettext("Are you looking for {0} work?", [label]);
    case PreferenceType.SEGMENTATION:
    case PreferenceType.OTHER_DETAILS:
      return getSegmentationQuestion(label, sectionTitle, i18n);
    case PreferenceType.SHIFT_AND_SCHEDULE:
    default:
      return getDefaultQuestion(label, i18n);
  }
};

export const getSalaryAmount = (preference: EnrichedJobDetailsPreference, locale: string) => {
  const formatter = Intl.NumberFormat(locale);
  const userMinimumPayAmount = preference.salaryInfoModel?.userMinimumPayAmount;

  if (preference.salaryInfoModel?.minimumPayPreferencePresent && userMinimumPayAmount) {
    return formatter.format(parseLocaleCurrencyAmount(userMinimumPayAmount.toString(), locale));
  } else if (
    preference.salaryInfoModel?.salaryMin !== undefined &&
    preference.salaryInfoModel?.salaryMin > 0
  ) {
    return formatter.format(
      parseLocaleCurrencyAmount(preference.salaryInfoModel?.salaryMin.toString(), locale),
    );
  } else {
    return undefined;
  }
};

export const getSalaryType = (preference: EnrichedJobDetailsPreference) => {
  const {
    minimumPayPreferencePresent,
    salaryType: jobSalaryType,
    userMinimumPaySalaryType: userSalaryType,
  } = preference.salaryInfoModel ?? {};

  if (minimumPayPreferencePresent && userSalaryType) {
    return userSalaryType;
  } else {
    return jobSalaryType;
  }
};

export const getSentimentFromSalaryInput = (
  salaryInfoModel: SalaryInfoModel,
  salaryInputs: SalaryInputs,
  userInputAmount: number,
) => {
  if (
    salaryInfoModel.salaryMax &&
    salaryInfoModel.salaryMin &&
    salaryInfoModel.salaryType === salaryInputs.salaryType
  ) {
    return userInputAmount <= salaryInfoModel?.salaryMin ||
      userInputAmount <= salaryInfoModel?.salaryMax
      ? Sentiment.POSITIVE
      : Sentiment.NEGATIVE;
  }
  return Sentiment.UNKNOWN;
};

type SalaryInputs = { amount?: string; salaryType?: string };

type PreferenceState = Record<string, EnrichedJobDetailsPreference>;
type PreferenceStateAction = {
  suid: string;
  sentiment?: Sentiment;
  prefIds?: string[];
};

const preferencesReducer = (
  state: PreferenceState,
  { suid, ...action }: PreferenceStateAction,
): PreferenceState => {
  return {
    ...state,
    [suid]: { ...state[suid], ...action },
  };
};

export const usePreferenceTiles = ({
  accountKey,
  knownUserSentimentIdMap = {},
  onAddError,
  preferenceType,
  sectionTitle,
  preferenceList = [],
  preferenceToTypeMap = new Map<string, PreferenceType>(),
  preferenceToSegmentationSectionMap = new Map<string, SegmentationSection>(),
}: {
  accountKey: string;
  knownUserSentimentIdMap: { [index: string]: string[] };
  onAddError: () => void;
  preferenceType: PreferenceType;
  sectionTitle: string;
  preferenceList?: EnrichedJobDetailsPreference[];
  preferenceToTypeMap?: Map<string, PreferenceType>;
  preferenceToSegmentationSectionMap?: Map<string, SegmentationSection>;
}) => {
  const gt = useI18n();
  const { country, language } = useLocale();
  const tareData = useTareData();
  const { mobvjtk: mobtk } = useViewjobOneGraphModel() ?? {};

  const loggedIn = tareData.accountStatus?.isLoggedIn ?? false;
  const locale = `${language}-${country}`;
  const { openCollectionModal } = useModalContext();
  const savePreferenceAndValidateResult = useSavePreferenceAndValidateResult();
  const resetPreference = useResetPreference();
  const addMinimumPayPreference = useAddMinimumPayPreference();

  const [preferencesMap, updatePreferenceState] = useReducer(
    preferencesReducer,
    preferenceList.reduce((acc, pref) => {
      acc[pref.suid] = {
        ...pref,
        prefIds: knownUserSentimentIdMap?.[pref.suid] || [],
      };

      return acc;
    }, {} as PreferenceState),
  );

  const preferences = useMemo(() => Object.values(preferencesMap), [preferencesMap]);

  useEffect(() => {
    preferences?.forEach((preference) => {
      if (![Sentiment.POSITIVE, Sentiment.NEGATIVE].includes(preference.sentiment)) {
        logIfQuestionReasked(mobtk ?? "", preference, accountKey);
      }
    });

    if ((preferenceList ?? []).length) {
      const customClassIds: string[][] = preferenceList?.map((pref) => pref.customClassSuids) ?? [];
      const customClassLabels: string[][] =
        preferenceList?.map((pref) => pref.customClassLabels) ?? [];
      logPreferenceSentimentSeen({
        mobtk: mobtk ?? "",
        attributeIds: AvroOptional.string(
          (preferenceList?.map((pref) => pref.suid) ?? []).join(","),
        ),
        customClassLabels: AvroOptional.string(customClassLabels.join()),
        customClassSuids: AvroOptional.string(customClassIds.join()),
        accountKey: AvroOptional.string(accountKey),
        section: AvroOptional.string(preferenceType.toString()),
      });
    }
  });

  const handleSalaryPreferenceSave = async (
    preference: EnrichedJobDetailsPreference,
    salaryInputs: SalaryInputs,

    sentimentClickLogData: Omit<PreferenceSentimentClick, "sentiment">,
  ) => {
    const hasPayRangeValidationError = validatePayRange(salaryInputs, locale, gt);
    if (
      !hasPayRangeValidationError &&
      salaryInputs?.amount &&
      salaryInputs?.salaryType &&
      preference.salaryInfoModel?.salaryMin !== undefined &&
      preference.salaryInfoModel?.salaryMax &&
      preference.salaryInfoModel?.salaryType &&
      parseLocaleCurrencyAmount(salaryInputs?.amount, "en_US") !== 0
    ) {
      try {
        const userInputAmount = parseLocaleCurrencyAmount(salaryInputs.amount, locale);
        const { currencyCode } = getLocaleCurrency(locale);
        const newSentiment = getSentimentFromSalaryInput(
          preference?.salaryInfoModel,
          salaryInputs,
          userInputAmount,
        );
        logPreferenceSentimentClick({
          ...sentimentClickLogData,
          sentiment: AvroOptional.string(newSentiment),
        });
        logPayPreferenceCollection({
          mobtk: mobtk ?? "",
          userInputPayAmount: AvroOptional.float(isNaN(userInputAmount) ? -1 : userInputAmount),
          jobDetailsPayAmount: AvroOptional.float(preference.salaryInfoModel.salaryMin),
          currency: AvroOptional.string(currencyCode),
          userInputPayFormatLabel: AvroOptional.string(salaryInputs.salaryType),
          userInputPayFormatValue: AvroOptional.int(
            SalaryTypeToValue.get(salaryInputs.salaryType.toUpperCase()) ?? -1,
          ),
          jobDetailsPayFormatLabel: AvroOptional.string(preference.salaryInfoModel.salaryType),
          jobDetailsPayFormatValue: AvroOptional.int(
            SalaryTypeToValue.get(preference.salaryInfoModel.salaryType.toUpperCase()) ?? -1,
          ),
          originalSentiment: AvroOptional.string(preference.sentiment),
          sentiment: AvroOptional.string(newSentiment),
        });

        const addedPreference = await addMinimumPayPreference({
          amount: userInputAmount,
          salaryType: salaryInputs.salaryType as JobSeekerProfileStructuredDataSalaryType,
          currency: currencyCode,
          sentiment: "POSITIVE",
        });

        // This will always be true because an exception will
        // be thrown in in the addMinimumPayPreference function
        // if the preference was not saved, but this satisfies typescript
        if (addedPreference.id) {
          updatePreferenceState({
            suid: preference.suid,
            prefIds: [addedPreference.id],
          });
        }

        if (
          preference?.salaryInfoModel?.userMinimumPaySalaryType &&
          preference?.salaryInfoModel?.userMinimumPayAmount !== undefined
        ) {
          preference.salaryInfoModel.userMinimumPayAmount = parseLocaleCurrencyAmount(
            salaryInputs.amount,
            locale,
          );
          preference.salaryInfoModel.userMinimumPaySalaryType = salaryInputs.salaryType;
          preference.salaryInfoModel.minimumPayPreferencePresent = true;
        }
        updatePreferenceState({
          suid: preference.suid,
          sentiment: newSentiment,
        });
        return true;
      } catch (error) {
        onAddError();
        return false;
      }
    }

    return false;
  };

  const getModalProps = ({
    preference,
    preferenceToTypeMap,
    preferenceToSegmentationSectionMap,
  }: {
    preference: EnrichedJobDetailsPreference;
    preferenceToTypeMap: Map<string, PreferenceType>;
    preferenceToSegmentationSectionMap: Map<string, SegmentationSection>;
  }): ContextModalProps => {
    const sentiment = preference.sentiment;

    return {
      title: sectionTitle,
      question: getPreferenceCollectionQuestion(
        preferenceToTypeMap?.get(preference.suid) ?? preferenceType,
        `[${preference.label}|attribute]`,
        preferenceToSegmentationSectionMap?.get(preference.suid) ?? sectionTitle,
        gt,
      ),
      data: {
        sentiment,
        salaryPreference: {
          salaryType: getSalaryType(preference),
          amount: getSalaryAmount(preference, locale),
        },
      },
      onModalSubmit: async ({ sentiment: newSentiment, salaryPreference }) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const { suid } = preference as EnrichedJobDetailsPreference;

        const sentimentClickLogData: PreferenceSentimentClick = {
          mobtk: mobtk ?? "",
          skip: false,
          reset: false,
          section: AvroOptional.string(preferenceType.toString()),
          suid: AvroOptional.string(suid),
          originalSentiment: AvroOptional.string(preference.sentiment),
        };

        try {
          if (salaryPreference && preferenceType === PreferenceType.SALARY) {
            return handleSalaryPreferenceSave(preference, salaryPreference, sentimentClickLogData);
          } else {
            logPreferenceSentimentClick({
              ...sentimentClickLogData,
              sentiment: AvroOptional.string(newSentiment),
            });
            const savedAttribute = await savePreferenceAndValidateResult(suid, newSentiment);
            updatePreferenceState({
              sentiment: newSentiment,
              suid: savedAttribute.suid,
              prefIds: [savedAttribute.id],
            });

            return true;
          }
        } catch (error) {
          onAddError();
          return false;
        }
      },
      onModalReset: async () => {
        logPreferenceSentimentClick({
          mobtk: mobtk ?? "",
          skip: false,
          reset: true,
          section: AvroOptional.string(preferenceType.toString()),
          suid: AvroOptional.string(preference.suid),
          sentiment: AvroOptional.string(Sentiment.UNKNOWN),
        });
        try {
          const resetSuccessful = await resetPreference(preference);

          if (resetSuccessful) {
            updatePreferenceState({
              sentiment: Sentiment.UNKNOWN,
              suid: preference.suid,
              prefIds: [],
            });
          } else {
            onAddError();
          }
        } catch (error) {
          onAddError();
        }

        return true;
      },
      onClose: () => {
        logInteractiveAttributeInteraction({
          mobtk: mobtk ?? "",
          suid: AvroOptional.string(preference?.suid),
          action: AvroOptional.string("CLOSE"),
          section: AvroOptional.string(preferenceType.toString()),
        });
      },
      showSalary: preferenceType === PreferenceType.SALARY,
    };
  };

  return preferences.map((preference: EnrichedJobDetailsPreference) => {
    const isNegativeMatch = preference.sentiment === Sentiment.NEGATIVE;
    const isMatch = preference.sentiment === Sentiment.POSITIVE;
    return {
      onPress: loggedIn
        ? () => {
            logInteractiveAttributeInteraction({
              mobtk: mobtk ?? "",
              suid: AvroOptional.string(preference.suid),
              action: AvroOptional.string("OPEN"),
              section: AvroOptional.string(preferenceType.toString()),
            });
            openCollectionModal(
              getModalProps({
                preference,
                preferenceToTypeMap,
                preferenceToSegmentationSectionMap,
              }),
              loggedIn,
            );
          }
        : undefined,
      variant: getTileVariant({
        isMatch,
        isNegativeMatch,
        isJobPreference: true,
      }),
      primaryText: preference.label,
      iconAriaLabel: isMatch ? gt.gettext("matching preference") : gt.gettext("missing preference"),
    };
  });
};
